import {
  SimpleGrid, GridItem,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import React, { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import {
  createItem,
  updateItem,
} from '@app/actions/common';
import TextField from '@app/components/chakra/fields/text-field';
import TextareaField from '@app/components/chakra/fields/textarea-field';
import { toaster } from '@app/components/chakra/snippets/toaster';
import { QUERY_KEYS } from '@app/services/constants';
import { Group } from '@app/types/api/group';
import { User } from '@app/types/api/user';
import { Button } from '@chakra-snippets/button';
import {
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
  DialogBody,
  DialogFooter,
} from '@chakra-snippets/dialog';

import AddMembersCard from './add-members-card';
import CurrentMembersAndGroups from './current-members-and-groups';

interface GroupDialogProps {
  isOpen: boolean;
  onOpenChange: (value: { open: boolean }) => void;
  editGroup?: Group;
  dialogTriggerButton?: React.ReactNode;
}

interface IFormInput {
  name: string;
  description: string;
  members: User[];
  leads: User[];
  subgroups: Group[];
}

const GroupDialog = ({
  isOpen,
  onOpenChange,
  editGroup,
  dialogTriggerButton,
}: GroupDialogProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const isEdit = !!editGroup?.id;
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const methods = useForm<IFormInput>({
    defaultValues: {
      name: editGroup?.name || '',
      description: editGroup?.description || '',
      members: editGroup?.members || [],
      leads: editGroup?.leads || [],
      subgroups: editGroup?.subgroups || [],
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (editGroup) {
      methods.reset(editGroup);
    }
  }, [editGroup, methods]);

  const {
    handleSubmit,
    formState: { isValid, isSubmitting },
    reset,
  } = methods;

  const handleFormSubmit = handleSubmit(async (data) => {
    if (isSubmitting) return;

    const updatedData = {
      id: editGroup?.id,
      name: data.name,
      description: data.description,
      members: data.members.map((member) => member.id) || [],
      leads: data.leads.map((lead) => lead.id) || [],
      subgroups: data.subgroups.map((group) => group.id) || [],
    };

    setIsLoading(true);
    try {
      if (isEdit) {
        await dispatch(updateItem('GROUP', updatedData, editGroup?.id, 'group'));
      } else {
        await dispatch(createItem('GROUP', updatedData, 'group'));
      }
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GROUPS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SUBGROUP] });
      onOpenChange({ open: false });
      toaster.create({
        title: 'Group saved successfully',
        type: 'success',
      });
      reset();
    } catch (error: unknown) {
      toaster.create({
        title: 'Failed to save group',
        description: error && typeof error === 'object' && 'message' in error
          ? String(error.message)
          : undefined,
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  });

  return (
    <DialogRoot size="xl" open={isOpen} onOpenChange={onOpenChange} scrollBehavior="inside" closeOnInteractOutside={false}>
      {dialogTriggerButton && <DialogTrigger>{dialogTriggerButton}</DialogTrigger>}
      <FormProvider {...methods}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isEdit ? 'Edit' : 'Create'}
              {' '}
              Group
            </DialogTitle>
          </DialogHeader>
          <DialogCloseTrigger />
          <DialogBody>
            <SimpleGrid columns={2} gap={4}>
              <GridItem colSpan={2}>
                <TextField<IFormInput>
                  label="Group name"
                  name="name"
                  registerOptions={{ required: 'Name is required' }}
                />
              </GridItem>
              <GridItem colSpan={2}>
                <TextareaField<IFormInput>
                  label="Group description"
                  name="description"
                />
              </GridItem>
              <AddMembersCard editGroupId={editGroup?.id} />
              <CurrentMembersAndGroups />
            </SimpleGrid>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange({ open: false })}>Cancel</Button>
            <Button type="submit" onClick={handleFormSubmit} disabled={!isValid} loading={isLoading}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </FormProvider>
    </DialogRoot>
  );
};

export default GroupDialog;
